import { useContext, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import axios from 'axios';
import { TranslationContext } from '../Context/TranslationContext';
import { useTranslation } from '../hooks/useTranslation';
import { LOCALE_BY_LANG } from '../Utils/i18nHelpers';

function formatDate(dateStr, locale) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(locale);
}

function formatMontant(montant, locale) {
    if (montant == null) return '—';
    return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(montant);
}

export default function InvoicesPage() {
    const t = useTranslation('Invoices.json');
    const { currentLanguage } = useContext(TranslationContext);
    const locale = LOCALE_BY_LANG[currentLanguage] || LOCALE_BY_LANG.fr;
    const STATUT_CONFIG = {
        payée:      { label: t('invoices.status.paid'),    color: 'success' },
        en_attente: { label: t('invoices.status.pending'), color: 'warning' },
        impayée:    { label: t('invoices.status.unpaid'),  color: 'error' },
    };
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(null);

    const serverUrl = process.env.REACT_APP_SERVER_URL_API || 'http://localhost:8090';

    useEffect(() => {
        axios.get(`${serverUrl}/api/invoices`)
            .then(res => setInvoices(res.data))
            .catch(() => setError(t('invoices.error')))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverUrl]);

    const handleDownload = async (invoice) => {
        setDownloading(invoice._id);
        try {
            const response = await axios.get(`${serverUrl}/api/invoices/${invoice._id}/download`, {
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', invoice.nom_original || 'facture.pdf');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            // silently ignore download errors
        } finally {
            setDownloading(null);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <ReceiptLongIcon sx={{ color: '#FF7233', fontSize: 32 }} />
                <Typography variant="h5" fontWeight={600}>
                    {t('invoices.title')}
                </Typography>
            </Box>

            {loading && (
                <Box display="flex" justifyContent="center" mt={4}>
                    <CircularProgress sx={{ color: '#FF7233' }} />
                </Box>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            {!loading && !error && invoices.length === 0 && (
                <Alert severity="info">{t('invoices.empty')}</Alert>
            )}

            {!loading && !error && invoices.length > 0 && (
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><Typography fontWeight={600}>{t('invoices.column.file')}</Typography></TableCell>
                                <TableCell><Typography fontWeight={600}>{t('invoices.column.date')}</Typography></TableCell>
                                <TableCell><Typography fontWeight={600}>{t('invoices.column.amount')}</Typography></TableCell>
                                <TableCell><Typography fontWeight={600}>{t('invoices.column.status')}</Typography></TableCell>
                                <TableCell align="center"><Typography fontWeight={600}>{t('invoices.column.download')}</Typography></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invoices.map((invoice) => {
                                const statut = STATUT_CONFIG[invoice.statut] || { label: invoice.statut, color: 'default' };
                                return (
                                    <TableRow key={invoice._id} hover>
                                        <TableCell>
                                            <Typography variant="body2">{invoice.nom_original}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{formatDate(invoice.date_facture, locale)}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{formatMontant(invoice.montant, locale)}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={statut.label}
                                                color={statut.color}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title={t('invoices.download_tooltip')}>
                                                <span>
                                                    <IconButton
                                                        onClick={() => handleDownload(invoice)}
                                                        disabled={downloading === invoice._id}
                                                        sx={{ color: '#FF7233' }}
                                                    >
                                                        {downloading === invoice._id
                                                            ? <CircularProgress size={20} sx={{ color: '#FF7233' }} />
                                                            : <DownloadIcon />
                                                        }
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}
